import React from 'react';
import {
  useQuery,
  NetworkStatus,
  usePaginationHelper,
  useLazyQuery,
} from '@common/hooks';
import {
  AddButton,
  buildOptionsFromData,
  EmptyContent,
  ErrorHandler,
  FilteredTable,
  Link,
  MultiSelectTableFilter,
  TimeWithTimeZone,
  WorksAvatar,
} from '@common/components';
import { Colors, FontSize, FontWeight } from '@common/tokens';
import { PaginatedCandidatesQuery } from 'works/legacy/graphql/works-profiles';
import {
  RolesQuery,
  WorksProfileType,
  ClinicalSpecialtyType,
} from '@clinician-profile/graphql';
import { useHasFilters } from 'works/legacy/lib/hooks/use-has-filters';
import { ClinicalSpecialtiesQuery } from '@institutions/graphql';
import { RoleType } from '@scheduling/graphql';
import { buildDialogUrl } from '@common/services';

const formatCandidate = (candidates: WorksProfileType[]) => {
  return candidates.map((candidate) => ({
    key: candidate.id,
    phone: candidate.phone,
    role: candidate.role?.abbreviation,
    primarySpecialty: candidate.primarySpecialty?.displayName,
    email: candidate.email,
    id: candidate.id,
    created: candidate.createdAt,
    name: {
      firstName: candidate.firstName,
      fullName: candidate.fullName,
      lastName: candidate.lastName,
      id: candidate.id,
    },
  }));
};

export function CandidatesTable() {
  const {
    filters,
    paginationVariables,
    localStorageFilterKey,
    tableChangeHandler,
    filterChangeHandler,
  } = usePaginationHelper({
    localStorageFilterKey: 'SupplierCandidatesTable',
    defaultPaginationData: {
      limit: 10,
      offset: 0,
      orderDirection: 'DESC',
      orderField: 'CREATED',
      defaultPageSize: 10,
    },
    columnsDictionary: {
      name: 'NAME',
      created: 'CREATED',
      role: 'ROLE',
      primarySpecialty: 'PRIMARY_SPECIALTY',
    },
  });

  const [fetchRoles, { error: supplierRolesError, data: supplierRolesData }] =
    useLazyQuery(RolesQuery);

  const [fetchSpecialties, { error: specialtiesError, data: specialtiesData }] =
    useLazyQuery(ClinicalSpecialtiesQuery);

  const { error, data, networkStatus } = useQuery(PaginatedCandidatesQuery, {
    variables: paginationVariables,
    notifyOnNetworkStatusChange: true,
    onCompleted() {
      fetchRoles();
      fetchSpecialties();
    },
  });

  const hasFilters = useHasFilters(filters);

  if (error || supplierRolesError || specialtiesError) {
    return <ErrorHandler error={error} log="CandidatesTable" />;
  }

  const columns = [
    {
      title: 'Candidate',
      dataIndex: 'name',
      key: 'name',
      width: 380,
      sorter: true,
      className: 'sentry-mask',
      render: (worksUserInfo: {
        firstName: string;
        lastName: string;
        id: string;
        fullName: string;
      }) => {
        return (
          <>
            <WorksAvatar
              firstName={worksUserInfo.firstName}
              lastName={worksUserInfo.lastName}
              size={40}
            />
            <Link
              to={`/works/supplier/candidates/${worksUserInfo.id}`}
              className="candidate-name"
            >
              {worksUserInfo.fullName}
            </Link>
            <style jsx>{`
              :global(a.candidate-name) {
                margin-left: 10px;
                font-weight: ${FontWeight.bold};
                font-size: ${FontSize.md};
                color: ${Colors.elm};
                vertical-align: middle;
              }
            `}</style>
          </>
        );
      },
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      sorter: true,
      filtered: filters.role || false,
      filteredValue: filters.role || null,
      filters: buildOptionsFromData(
        supplierRolesData?.roles || [],
        (role: RoleType) => ({
          value: role.id,
          text: role.abbreviation,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      title: 'Specialty',
      dataIndex: 'primarySpecialty',
      key: 'primarySpecialty',
      sorter: true,
      filtered: filters.primarySpecialty || false,
      filteredValue: filters.primarySpecialty || null,
      filters: buildOptionsFromData(
        specialtiesData?.clinicalSpecialties || [],
        (specialty: ClinicalSpecialtyType) => ({
          value: specialty.id,
          text: specialty.displayName,
        })
      ),
      filterDropdown: MultiSelectTableFilter,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      className: 'sentry-mask',
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      className: 'sentry-mask',
    },
    {
      title: 'Created',
      dataIndex: 'created',
      key: 'created',
      defaultSortOrder: 'descend',
      sorter: true,
      render: (createdAt: moment.Moment) => (
        <TimeWithTimeZone date={createdAt} variant="short_format" />
      ),
    },
  ];

  const isLoaded = networkStatus === NetworkStatus.ready && data;

  const formattedCandidates = isLoaded
    ? formatCandidate(data.paginatedWorksProfiles.worksProfiles)
    : [];

  const total = isLoaded ? data.paginatedWorksProfiles.totalCount : null;

  if (!hasFilters && isLoaded && formattedCandidates.length === 0) {
    return (
      <EmptyContent
        imagePath="./supplier/candidates/empty-candidates.png"
        header="O’ candidates, where art thou?"
        text="Begin by adding new candidates above. Candidates you’ve added can be submitted to Staffing Requests as they become available."
      />
    );
  }

  const createCandidateDrawerUrl = buildDialogUrl('create-candidate-drawer');

  return (
    <FilteredTable
      rowKey="id"
      columns={columns}
      dataSource={formattedCandidates}
      loading={!isLoaded}
      pagination={{
        ...paginationVariables,
        total,
      }}
      onChange={tableChangeHandler}
      onFilterChange={filterChangeHandler}
      localStorageFilterKey={localStorageFilterKey}
      filters={filters}
      headerRightSlot={
        <Link to={createCandidateDrawerUrl}>
          <AddButton>Candidate</AddButton>
        </Link>
      }
    />
  );
}
